import React from 'react';
import { Box, Grid, Paper, Typography } from '@mui/material';
import type { PatternVolumeKind } from '../../domain/incidents/patternReviewScopeSemantics';
import {
  ACTION_OWNER_ROLE_HELPER,
  ownerRoleIsUnassigned,
  RISK_OWNER_ROLE_HELPER,
} from '../../domain/incidents/incidentPatternVocabulary';
import type { ReviewWindowContext } from '../../domain/incidents/reviewWindowContext';
import {
  formatUnderstandRiskActionOwnerLine,
  formatUnderstandRiskFollowUpStatus,
  formatUnderstandRiskNextReview,
  formatUnderstandRiskWorkOrder,
} from '../../domain/incidents/groupedUnderstandRiskPresentation';
import { displaySafetyManagerLabel } from '../../domain/safetyManagerUxLabels';

export type GroupedUnderstandRiskScanCardsProps = {
  patternVolumeCount: number;
  patternVolumeKind?: PatternVolumeKind;
  reviewWindowContext: ReviewWindowContext;
  locationLine: string;
  policyLabel: string;
  patternOwner: string;
  followUpOwner: string;
  followUpStatus: string;
  workflowStatus?: string;
  nextReviewSummary: string;
  workOrderSummary: string;
};

type ScanCardProps = {
  title: string;
  testId: string;
  children: React.ReactNode;
};

const ScanCard: React.FC<ScanCardProps> = ({ title, testId, children }) => (
  <Paper
    variant="outlined"
    data-testid={testId}
    sx={{
      p: 1.5,
      height: '100%',
      borderRadius: 2.5,
      borderColor: '#d0d7e2',
      bgcolor: '#ffffff',
    }}
  >
    <Typography
      sx={{
        fontSize: 10.5,
        letterSpacing: 0.85,
        color: 'text.secondary',
        textTransform: 'uppercase',
        fontWeight: 800,
        mb: 0.75,
      }}
    >
      {title}
    </Typography>
    {children}
  </Paper>
);

const CardLine: React.FC<{ children: React.ReactNode; secondary?: boolean }> = ({
  children,
  secondary = false,
}) => (
  <Typography
    variant="body2"
    color={secondary ? 'text.secondary' : 'text.primary'}
    sx={{ fontWeight: secondary ? 500 : 700, lineHeight: 1.5, mb: secondary ? 0 : 0.35 }}
  >
    {children}
  </Typography>
);

export const GroupedUnderstandRiskScanCards: React.FC<GroupedUnderstandRiskScanCardsProps> = (props) => {
  const {
    patternOwner,
    followUpOwner,
    followUpStatus,
    workflowStatus,
    nextReviewSummary,
    workOrderSummary,
  } = props;
  const riskOwnerLabel = displaySafetyManagerLabel('pattern_owner');
  const actionOwnerLabel = displaySafetyManagerLabel('followup_owner');
  const normalizedRiskOwner = ownerRoleIsUnassigned(patternOwner) ? 'Unassigned' : patternOwner;
  const actionOwnerUnassigned = ownerRoleIsUnassigned(followUpOwner);
  const actionOwnerLine = formatUnderstandRiskActionOwnerLine(followUpOwner, actionOwnerLabel);

  return (
    <Grid container spacing={1.5} sx={{ mb: 2 }} data-testid="grouped-understand-risk-scan-cards">
      <Grid item xs={12} sm={6}>
        <ScanCard title="Ownership" testId="understand-risk-card-ownership">
          <CardLine>
            {riskOwnerLabel}
            :
            {' '}
            {normalizedRiskOwner}
          </CardLine>
          <CardLine secondary>{RISK_OWNER_ROLE_HELPER}</CardLine>
          <Box sx={{ mt: 0.75 }}>
            <CardLine>{actionOwnerLine}</CardLine>
            {!actionOwnerUnassigned ? (
              <CardLine secondary>{ACTION_OWNER_ROLE_HELPER}</CardLine>
            ) : null}
          </Box>
        </ScanCard>
      </Grid>
      <Grid item xs={12} sm={6}>
        <ScanCard title="Follow-up" testId="understand-risk-card-followup">
          <CardLine>
            Status:
            {' '}
            {formatUnderstandRiskFollowUpStatus(followUpStatus, workflowStatus)}
          </CardLine>
          <CardLine>
            Next review:
            {' '}
            {formatUnderstandRiskNextReview(nextReviewSummary)}
          </CardLine>
          <CardLine>
            Work order:
            {' '}
            {formatUnderstandRiskWorkOrder(workOrderSummary)}
          </CardLine>
        </ScanCard>
      </Grid>
    </Grid>
  );
};

export default GroupedUnderstandRiskScanCards;
